// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/configuracoes/hooks/useConfiguracoes.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import configuracoesService from '../services/configuracoesService';
import type {
  AtualizarConfigNfeRequest,
  AtualizarConfigNfceRequest,
  AtualizarConfigMdfeRequest,
  AtualizarConfigCteRequest,
  AtualizarConfigCteOsRequest,
  AtualizarConfigCertificadoRequest,
  AtualizarConfigSistemaRequest,
  AtualizarConfigReformaRequest,
  SalvarEmitenteFiscalRequest,
} from '../types';

// ── Emitente fiscal (serviço de emissão + certificado A1) ────────────────────
export function useEmitenteFiscal(empresaId: number | undefined) {
  const queryClient = useQueryClient();
  const chave = ['emitente-fiscal', empresaId];

  const query = useQuery({
    queryKey: chave,
    queryFn: () => configuracoesService.getEmitenteFiscal(empresaId!),
    enabled: !!empresaId,
    staleTime: 60_000,
    retry: false,
  });

  const { mutate: salvar, isPending: isSalvando } = useMutation({
    mutationFn: (req: SalvarEmitenteFiscalRequest) =>
      configuracoesService.salvarEmitenteFiscal(empresaId!, req),
    onSuccess: (data) => {
      queryClient.setQueryData(chave, data);
      toast.success('Dados de emissão salvos!');
    },
    onError: (err) => toast.error('Não foi possível salvar os dados de emissão.', {
      description: getApiErrorMessage(err),
    }),
  });

  const { mutate: enviarCertificado, isPending: isEnviandoCertificado } = useMutation({
    mutationFn: (p: { arquivo: File; senha: string }) =>
      configuracoesService.enviarCertificadoFiscal(empresaId!, p.arquivo, p.senha),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chave });
      toast.success('Certificado instalado!');
    },
    onError: (err) => toast.error('Certificado recusado.', {
      description: getApiErrorMessage(err),
    }),
  });

  const { mutate: testarSefaz, isPending: isTestandoSefaz, data: statusSefaz } = useMutation({
    mutationFn: () => configuracoesService.statusSefaz(empresaId!),
    onError: (err) => toast.error('Falha ao consultar a SEFAZ.', {
      description: getApiErrorMessage(err),
    }),
  });

  return {
    ...query, salvar, isSalvando, enviarCertificado, isEnviandoCertificado,
    testarSefaz, isTestandoSefaz, statusSefaz,
  };
}

// ── NF-e ─────────────────────────────────────────────────────────────────────

export function useConfigNfe(empresaId: number | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['config-nfe', empresaId],
    queryFn: () => configuracoesService.getConfigNfe(empresaId!),
    enabled: !!empresaId,
    staleTime: 60_000,
    retry: false,
  });

  const { mutate: salvar, isPending: isSalvando } = useMutation({
    mutationFn: (req: AtualizarConfigNfeRequest) =>
      configuracoesService.salvarConfigNfe(empresaId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-nfe', empresaId] });
      toast.success('Configurações de NF-e salvas!');
    },
    onError: (err) => toast.error('Erro ao salvar configurações de NF-e.', {
      description: getApiErrorMessage(err),
    }),
  });

  return { ...query, salvar, isSalvando };
}

// ── NFC-e ────────────────────────────────────────────────────────────────────

export function useConfigNfce(empresaId: number | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['config-nfce', empresaId],
    queryFn: () => configuracoesService.getConfigNfce(empresaId!),
    enabled: !!empresaId,
    staleTime: 60_000,
    retry: false,
  });

  const { mutate: salvar, isPending: isSalvando } = useMutation({
    mutationFn: (req: AtualizarConfigNfceRequest) =>
      configuracoesService.salvarConfigNfce(empresaId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-nfce', empresaId] });
      toast.success('Configurações de NFC-e salvas!');
    },
    onError: (err) => toast.error('Erro ao salvar configurações de NFC-e.', {
      description: getApiErrorMessage(err),
    }),
  });

  return { ...query, salvar, isSalvando };
}

// ── MDF-e ────────────────────────────────────────────────────────────────────

export function useConfigMdfe(empresaId: number | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['config-mdfe', empresaId],
    queryFn: () => configuracoesService.getConfigMdfe(empresaId!),
    enabled: !!empresaId,
    staleTime: 60_000,
    retry: false,
  });

  const { mutate: salvar, isPending: isSalvando } = useMutation({
    mutationFn: (req: AtualizarConfigMdfeRequest) =>
      configuracoesService.salvarConfigMdfe(empresaId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-mdfe', empresaId] });
      toast.success('Configurações de MDF-e salvas!');
    },
    onError: (err) => toast.error('Erro ao salvar configurações de MDF-e.', {
      description: getApiErrorMessage(err),
    }),
  });

  return { ...query, salvar, isSalvando };
}

// ── CT-e ─────────────────────────────────────────────────────────────────────

export function useConfigCte(empresaId: number | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['config-cte', empresaId],
    queryFn: () => configuracoesService.getConfigCte(empresaId!),
    enabled: !!empresaId,
    staleTime: 60_000,
    retry: false,
  });

  const { mutate: salvar, isPending: isSalvando } = useMutation({
    mutationFn: (req: AtualizarConfigCteRequest) =>
      configuracoesService.salvarConfigCte(empresaId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-cte', empresaId] });
      toast.success('Configurações de CT-e salvas!');
    },
    onError: (err) => toast.error('Erro ao salvar configurações de CT-e.', {
      description: getApiErrorMessage(err),
    }),
  });

  return { ...query, salvar, isSalvando };
}

// ── CT-e OS ──────────────────────────────────────────────────────────────────

export function useConfigCteOs(empresaId: number | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['config-cteos', empresaId],
    queryFn: () => configuracoesService.getConfigCteOs(empresaId!),
    enabled: !!empresaId,
    staleTime: 60_000,
    retry: false,
  });

  const { mutate: salvar, isPending: isSalvando } = useMutation({
    mutationFn: (req: AtualizarConfigCteOsRequest) =>
      configuracoesService.salvarConfigCteOs(empresaId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-cteos', empresaId] });
      toast.success('Configurações de CT-e OS salvas!');
    },
    onError: (err) => toast.error('Erro ao salvar configurações de CT-e OS.', {
      description: getApiErrorMessage(err),
    }),
  });

  return { ...query, salvar, isSalvando };
}

// ── Certificado ───────────────────────────────────────────────────────────────

export function useConfigCertificado(empresaId: number | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['config-certificado', empresaId],
    queryFn: () => configuracoesService.getConfigCertificado(empresaId!),
    enabled: !!empresaId,
    staleTime: 60_000,
    retry: false,
  });

  const { mutate: salvar, isPending: isSalvando } = useMutation({
    mutationFn: (req: AtualizarConfigCertificadoRequest) =>
      configuracoesService.salvarConfigCertificado(empresaId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-certificado', empresaId] });
      toast.success('Certificado digital atualizado!');
    },
    onError: (err) => toast.error('Erro ao salvar certificado digital.', {
      description: getApiErrorMessage(err),
    }),
  });

  return { ...query, salvar, isSalvando };
}

// ── Sistema ───────────────────────────────────────────────────────────────────

export function useConfigSistema(empresaId: number | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['config-sistema', empresaId],
    queryFn: () => configuracoesService.getConfigSistema(empresaId!),
    enabled: !!empresaId,
    staleTime: 60_000,
    retry: false,
  });

  const { mutate: salvar, isPending: isSalvando } = useMutation({
    mutationFn: (req: AtualizarConfigSistemaRequest) =>
      configuracoesService.salvarConfigSistema(empresaId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-sistema', empresaId] });
      toast.success('Parâmetros do sistema salvos!');
    },
    onError: (err) => toast.error('Erro ao salvar parâmetros do sistema.', {
      description: getApiErrorMessage(err),
    }),
  });

  return { ...query, salvar, isSalvando };
}

// ── Reforma Tributária (IBS/CBS) ──────────────────────────────────────────────

export function useConfigReforma(empresaId: number | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['config-reforma', empresaId],
    queryFn: () => configuracoesService.getConfigReforma(empresaId!),
    enabled: !!empresaId,
    staleTime: 60_000,
    retry: false,
  });

  const { mutate: salvar, isPending: isSalvando } = useMutation({
    mutationFn: (req: AtualizarConfigReformaRequest) =>
      configuracoesService.salvarConfigReforma(empresaId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-reforma', empresaId] });
      toast.success('Configurações da Reforma Tributária salvas!');
    },
    onError: (err) => toast.error('Erro ao salvar configurações da Reforma Tributária.', {
      description: getApiErrorMessage(err),
    }),
  });

  return { ...query, salvar, isSalvando };
}
